import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { VerificationProcessService } from "../../../Component/Api/VerificationAgency/VerificationProcess/VerificationProcessService";
import { AppDatepicker } from "../../../Component/Common/Components/FormValidationInputs/AppDatepicker";
import { AppQuill } from "../../../Component/Common/Components/FormValidationInputs/AppQuill";
import AppSelectList, { IDropDownOptions } from "../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { StatusTypeList } from "../../../Component/Common/Constants/Lists/DropdownListOptions";
import { VerificationProcess } from "../../../Component/Model/Dto/VerificationAgency/VerificationProcessTracker";
import { VerificationStatusTrackerScheme } from "./VerificationStatusTrackerValidation";


interface IProps {
    scorecardId: string
    showScorecardVerificationProcessTrackerModal: boolean;
    closeScorecardVerificationProcessTrackerModal: () => void;
    onRefresh: () => void;

}

export const VerificationProcessTrackerEdit = (props: IProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [verificationProcessTypes, setVerificationProcessTypes] = useState<IDropDownOptions[]>([]);
    const [haveVerificationProcessTypes, setHaveVerificationprocessTypes] = useState<boolean>(false);
    const getVerificationProcessTypes = async (id: string) => {
        try {
            setIsLoading(true);
            const result = await VerificationProcessService.GetVerificationProcessTypes();
            if (result.data.length > 0) {
                setHaveVerificationprocessTypes(true);
            }
            const mappedData = result.data.map(item => ({
                id: item.id,
                value: item.description
            }));

            setVerificationProcessTypes(mappedData);

        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(VerificationStatusTrackerScheme)
    });

    useEffect(() => {
        getVerificationProcessTypes(props.scorecardId)
    }, []);

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const finalData = {
                id: 0,
                note: data.note,
                scheduledDate: data.scheduledDate,
                scorecardId: props.scorecardId,
                statusDate: data.statusDate,
                verificationProcessTypeId: data.verificationProcessTypeId,
                verificationStatus: data.verificationStatus
            } as VerificationProcess.VerificationProcessDto;
            const result = await VerificationProcessService.SaveVerificationProcessDetails(finalData);
            toast.success("Changes saved successfully");
            props.onRefresh();
        } catch (error) {
            console.log(error)
        } finally {
            getVerificationProcessTypes(props.scorecardId);
            reset();
        }
    }

    return (
        <>
            <Box>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <form id='SubmitForm' onSubmit={handleSubmit(handleDataSubmit)}>
                        <Grid container paddingTop={1} paddingBottom='0px'>
                            <Grid item xs={3}>
                                <MDTypography variant='body' sx={{fontWeight: 500}}>Verification Process</MDTypography>
                            </Grid>
                            <Grid item xs={5}>
                                {haveVerificationProcessTypes ? (
                                    <AppSelectList
                                        control={control}
                                        name="verificationProcessTypeId"
                                        label="Status"
                                        items={verificationProcessTypes}
                                    />
                                ) : (
                                    <Typography style={{ fontSize: '12px', color: 'red' }}>
                                        No verification process types found
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        <Grid container paddingTop={1} paddingBottom='0px'>
                            <Grid item xs={3}>
                                <MDTypography variant='body' sx={{fontWeight: 500}}>Status</MDTypography>
                            </Grid>
                            <Grid item xs={5}>
                                <AppSelectList control={control}
                                    name="verificationStatus"
                                    label="Status"
                                    items={StatusTypeList} />
                            </Grid>
                        </Grid>

                        <Grid container paddingTop={1} paddingBottom='0px'>
                            <Grid item xs={3}>
                                <MDTypography variant='body' sx={{fontWeight: 500}}>Scheduled Date</MDTypography>
                            </Grid>
                            <Grid item xs={5}>
                                <AppDatepicker
                                    control={control}
                                    name="scheduledDate"
                                    label="Schedule Date"
                                    inputFormat='yyyy/MM/dd'
                                    minDate={new Date(1900, 1, 1)} />
                            </Grid>
                        </Grid>

                        <Grid container paddingTop={1} paddingBottom='0px'>
                            <Grid item xs={3}>
                                <MDTypography variant='body' sx={{fontWeight: 500}}>Status Date</MDTypography>
                            </Grid>
                            <Grid item xs={5}>
                                <AppDatepicker
                                    control={control}
                                    name="statusDate"
                                    label="Status Date"
                                    inputFormat='yyyy/MM/dd'
                                    minDate={new Date(1900, 1, 1)} />
                            </Grid>
                        </Grid>
                        <Grid container paddingTop={1} paddingBottom='0px'>
                            <Grid item xs={3}>
                                <MDTypography variant='body' sx={{fontWeight: 500}}>Note</MDTypography>
                            </Grid>
                        </Grid>
                        <MDBox mt={1} className="leftAlign" >
                            <MDTypography variant='button' fontWeight='regular' style={{ color: '#666666', fontSize: "0.85em" }}>
                                Note (Optional, Max 1000 characters)
                            </MDTypography>
                            <AppQuill control={control} name="note" placeholder="Write something..." />
                        </MDBox>
                        <Grid container paddingTop={1} paddingBottom='0px'>
                            <Grid item xs={3}>
                                <LoadingButton
                                    loading={isSubmitting}
                                    variant='contained'
                                    form='SubmitForm'
                                    type="submit"
                                    color='primary'
                                    sx={{ marginRight: '10px' }} 
                                    disabled={!haveVerificationProcessTypes} >
                                    Add
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Box>
        </>
    )
}